import { useMemo } from 'react'

import { amountToString, areAmountsEqual, zeroAmount, type CurrencyPosition } from '@orus.eu/amount'
import {
  getAmountFromAmountAndRecurrence,
  type ProductAttributeDescription,
  type ProductAttributes,
  type ProductAttributeValue,
} from '@orus.eu/dimensions'
import { type AnyLocalizableMessage, type Language, resolvePolicyText, translate } from '@orus.eu/translations'
import { buildStandaloneTermRegex } from '@orus.eu/utils'

import { useLanguage } from '../localization/index.ts'

export type ProjectedProductAttributeDescription = ProductAttributeValue & {
  label: string
  tooltip: string | undefined
  highlightedTerm: string | undefined
}

export type ProjectedProductAttributes = {
  productName: string
  attributes: readonly ProjectedProductAttributeDescription[]
}

/**
 * Projects an array of attributes using a project function
 */
export function useProductAttributesProjection(
  attributes: ProductAttributeDescription[],
  projection: (
    attribute: ProductAttributeDescription,
    language: Language,
  ) => ProjectedProductAttributeDescription | undefined,
): ProjectedProductAttributeDescription[] {
  const language = useLanguage()
  return useMemo(() => projectProductAttributes(attributes, language, projection), [attributes, language, projection])
}

/**
 * Project several array of attributes grouped by product using a project function
 */
export function useProductsAttributesProjection(
  productsAttributes: readonly ProductAttributes[],
  projection: (
    attribute: ProductAttributeDescription,
    language: Language,
  ) => ProjectedProductAttributeDescription | undefined,
): Array<{ productName: string; attributes: readonly ProjectedProductAttributeDescription[] }> {
  const language = useLanguage()
  return useMemo<Array<{ productName: string; attributes: ProjectedProductAttributeDescription[] }>>(
    () =>
      productsAttributes
        .map(({ productName, attributes }) => ({
          productName: resolvePolicyText(productName, language),
          attributes: projectProductAttributes(attributes, language, projection),
        }))
        .filter(({ attributes }) => attributes.length > 0),
    [productsAttributes, language, projection],
  )
}

function projectProductAttributes(
  attributes: ProductAttributeDescription[],
  language: Language,
  projection: (
    attribute: ProductAttributeDescription,
    language: Language,
  ) => ProjectedProductAttributeDescription | undefined,
): ProjectedProductAttributeDescription[] {
  return attributes.flatMap((attribute): Array<ProjectedProductAttributeDescription> => {
    const projectionOutput = projection(attribute, language)
    return projectionOutput ? [projectionOutput] : []
  })
}

export function projectAttributeInProductBlock(
  attribute: ProductAttributeDescription,
  language: Language,
): ProjectedProductAttributeDescription | undefined {
  return attribute.inProductContext
    ? projectAttributeWithLabel(attribute, translateAttribute(attribute.inProductContext, language), language)
    : undefined
}

export function projectAttributeInQuoteDetailDialog(
  attribute: ProductAttributeDescription,
  language: Language,
): ProjectedProductAttributeDescription | undefined {
  return attribute.inQuoteDetailContext
    ? projectAttributeWithLabel(attribute, translateAttribute(attribute.inQuoteDetailContext, language), language)
    : undefined
}

export function projectAttributeInQuoteDetailBody(
  attribute: ProductAttributeDescription,
  language: Language,
): ProjectedProductAttributeDescription | undefined {
  return attribute.inQuoteDetailBodyContext
    ? {
        label: translate(attribute.inQuoteDetailBodyContext.message, language),
        tooltip: undefined,
        highlightedTerm: undefined,
        ...extractProductAttributeValue(attribute),
      }
    : undefined
}
export function projectAttributeInSummaryUnderQuote(
  attribute: ProductAttributeDescription,
  language: Language,
): ProjectedProductAttributeDescription | undefined {
  return attribute.inGeneralSummary
    ? projectAttributeWithLabel(attribute, translateAttribute(attribute.inGeneralSummary, language), language)
    : undefined
}

function projectAttributeWithLabel(
  attribute: ProductAttributeDescription,
  label: string,
  language: Language,
): ProjectedProductAttributeDescription {
  const tooltip = translateAttributeTooltip(attribute, language)
  const { highlightedTerm } = attribute
  return {
    label,
    tooltip,
    highlightedTerm:
      tooltip !== undefined && highlightedTerm !== undefined && buildStandaloneTermRegex(highlightedTerm).test(label)
        ? highlightedTerm
        : undefined,
    ...extractProductAttributeValue(attribute),
  }
}

function translateAttributeTooltip(
  value: { tooltip?: string } | { localizableTooltip?: AnyLocalizableMessage },
  language: Language,
): string | undefined {
  return 'tooltip' in value
    ? value.tooltip
    : 'localizableTooltip' in value && value.localizableTooltip
      ? translate(value.localizableTooltip, language)
      : undefined
}

function translateAttribute(
  value: { label: string } | { localizableLabel: AnyLocalizableMessage },
  language: Language,
): string {
  return 'label' in value ? value.label : translate(value.localizableLabel, language)
}

export function productAttributeValueToString(
  productAttribute: ProductAttributeValue,
  language: Language,
  currencyPosition?: CurrencyPosition,
): string {
  const amountToStringOptions = {
    addCurrency: true,
    displayDecimals: 'when-amount-has-cents',
    ...(currencyPosition ? { currencyPosition } : {}),
  } as const

  switch (productAttribute.type) {
    case 'string':
      return productAttribute.value
    case 'included':
      return translate(productAttribute.value ? 'included' : 'not_included', language)
    case 'deductible':
      return areAmountsEqual(productAttribute.value, zeroAmount)
        ? translate('deductible_none', language)
        : amountToString(productAttribute.value, amountToStringOptions)
    case 'limit':
    case 'amount':
      return amountToString(productAttribute.value, amountToStringOptions)
    case 'amount-and-recurrence': {
      const amountFromProductAttribute = productAttribute.value
        ? getAmountFromAmountAndRecurrence(productAttribute.value)
        : zeroAmount
      return amountToString(amountFromProductAttribute ?? zeroAmount, amountToStringOptions)
    }
  }
}

function extractProductAttributeValue(productAttribute: ProductAttributeDescription): ProductAttributeValue {
  const { type, value } = productAttribute
  return { type, value } as ProductAttributeValue
}
